import React, { useEffect, useState } from "react";
import { Card, SecondaryButton, PrimaryButton, ErrorBanner } from "./ui";
import PaymentModal from "./PaymentModal";
import LoginModal from "./LoginModal";
import { getPoojaDetail, getPoojaAstrologers, initiateBooking, bookPooja } from "../api/poojaApi";
import { isLoggedIn } from "../utils/authStorage";

const FALLBACK_PANDITS = [
  { id: "51", full_name: "Acharya Ramdev Shastri" },
  { id: "52", full_name: "Pandit Dinesh K. Dixit" },
  { id: "53", full_name: "Guru Rajendra Prasad" }
];

export default function PoojaDetail({ slug, onBack }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Scheduling states
  const [pandits, setPandits] = useState([]);
  const [selectedPanditId, setSelectedPanditId] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");

  const [couponCode, setCouponCode] = useState("");
  const [useWallet, setUseWallet] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  const [stage, setStage] = useState(null); // "login" | "payment" | null
  const [booked, setBooked] = useState(false);
  const [bookError, setBookError] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getPoojaDetail(slug)
      .then((res) => {
        setDetail(res.data);
        if (res.data?.default_astrologer?.id) {
          setSelectedPanditId(String(res.data.default_astrologer.id));
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));

    // Fetch available Pandit Jis
    getPoojaAstrologers(slug)
      .then((res) => {
        const list = (res.data || []).map((p, index) => ({
          id: String(p.id),
          full_name: p.full_name || FALLBACK_PANDITS[index % FALLBACK_PANDITS.length].full_name
        }));
        setPandits(list.length > 0 ? list : FALLBACK_PANDITS);
      })
      .catch(() => {
        setPandits(FALLBACK_PANDITS);
      });

    // Set default schedule time to tomorrow at 10 AM
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(10, 0, 0, 0);
    const tzoffset = tomorrow.getTimezoneOffset() * 60000;
    const localISOTime = new Date(tomorrow - tzoffset).toISOString().slice(0, 16);
    setScheduledAt(localISOTime);
  }, [slug]);

  const bookingPayload = () => ({
    coupon_code: couponCode.trim(),
    use_wallet: useWallet,
    astrologer_id: selectedPanditId ? Number(selectedPanditId) : undefined,
    scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : undefined,
  });

  const previewPrice = async () => {
    if (!scheduledAt) {
      setSummaryError("Please select a date and time first.");
      return;
    }
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const res = await initiateBooking(slug, bookingPayload());
      setSummary(res.data);
    } catch (err) {
      setSummaryError(err.message);
    } finally {
      setSummaryLoading(false);
    }
  };

  const startBooking = async () => {
    if (!scheduledAt) {
      setBookError("Please select a date and time for the Pooja.");
      return;
    }
    setBookError(null);
    if (summary && Number(summary.razorpay_amount) === 0) {
      setBookingLoading(true);
      try {
        await bookPooja(slug, bookingPayload());
        setBooked(true);
      } catch (err) {
        setBookError(err.message);
      } finally {
        setBookingLoading(false);
      }
      return;
    }
    setStage("payment");
  };

  const handleBookClick = () => {
    if (!scheduledAt) {
      setBookError("Please select a date and time for the Pooja.");
      return;
    }
    if (!isLoggedIn()) {
      setStage("login");
      return;
    }
    startBooking();
  };

  if (loading) return <div style={styles.loading}>Loading pooja details…</div>;
  if (error) return <ErrorBanner message={error} />;
  if (!detail) return null;

  if (booked) {
    return (
      <Card>
        <div style={styles.successBox}>
          ✅ Your booking for <strong>{detail.title}</strong> is confirmed! You can track it
          anytime under "My Bookings" / "Dashboard", and your assigned Pandit Ji will join you for the video call session.
        </div>
        <SecondaryButton onClick={onBack} style={{ marginTop: 16 }}>
          ← Back to Poojas
        </SecondaryButton>
      </Card>
    );
  }

  return (
    <div style={styles.wrap}>
      <SecondaryButton onClick={onBack}>← Back to Poojas</SecondaryButton>

      <Card style={{ marginTop: 14 }}>
        {detail.images?.[0]?.url && (
          <img src={detail.images[0].url} alt={detail.title} style={styles.heroImg} />
        )}

        <div style={styles.tagsRow}>
          {(detail.categories || []).map((c) => (
            <span
              key={c.id}
              style={{ ...styles.tag, background: c.color_hex, color: c.text_color }}
            >
              {c.name}
            </span>
          ))}
        </div>

        <h2 style={styles.title}>{detail.title}</h2>
        <p style={styles.description}>{detail.description}</p>

        {/* Pandit Ji & Schedule Form */}
        <div style={styles.schedulingBox}>
          <h3 style={styles.schedulingHeading}>📅 Schedule Your Pooja</h3>
          <div style={styles.formGroup}>
            <label style={styles.label}>Select Pandit Ji / Astrologer</label>
            <select
              className="km-input"
              style={styles.selectInput}
              value={selectedPanditId}
              onChange={(e) => setSelectedPanditId(e.target.value)}
            >
              {pandits.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.full_name}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Select Date &amp; Time</label>
            <input
              className="km-input"
              type="datetime-local"
              style={styles.dateTimeInput}
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              min={new Date().toISOString().slice(0, 16)}
            />
          </div>
        </div>

        {detail.benefits?.length > 0 && (
          <div style={styles.benefits}>
            <p style={styles.benefitsTitle}>What This Pooja Does</p>
            <ul style={styles.benefitsList}>
              {detail.benefits.map((b) => (
                <li key={b.id} style={styles.benefitItem}>
                  {b.emoji && <span style={{ marginRight: 8 }}>{b.emoji}</span>}
                  {b.text}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div style={styles.priceBox}>
          <div style={styles.priceRow}>
            <span>Price</span>
            <span>₹{detail.price}</span>
          </div>
          <div style={styles.priceRow}>
            <span>GST ({detail.gst_percent}%)</span>
            <span>₹{detail.gst_amount}</span>
          </div>
          <div style={{ ...styles.priceRow, ...styles.priceTotalRow }}>
            <span>Total</span>
            <span>₹{detail.total_payable}</span>
          </div>
        </div>

        <div style={styles.couponRow}>
          <input
            className="km-input"
            style={styles.couponInput}
            placeholder="Coupon code (optional)"
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value)}
          />
          <label style={styles.walletLabel}>
            <input
              type="checkbox"
              checked={useWallet}
              onChange={(e) => setUseWallet(e.target.checked)}
            />
            Use wallet balance
          </label>
          <SecondaryButton onClick={previewPrice} disabled={summaryLoading}>
            {summaryLoading ? "Checking…" : "Apply / Preview Price"}
          </SecondaryButton>
        </div>

        <ErrorBanner message={summaryError} />

        {summary && (
          <div style={styles.summaryBox}>
            {summary.coupon_error && <div style={styles.summaryError}>{summary.coupon_error}</div>}
            {Number(summary.coupon_discount) > 0 && (
              <div style={styles.priceRow}>
                <span>Coupon discount</span>
                <span>-₹{summary.coupon_discount}</span>
              </div>
            )}
            {Number(summary.wallet_to_use) > 0 && (
              <div style={styles.priceRow}>
                <span>Wallet balance used</span>
                <span>-₹{summary.wallet_to_use}</span>
              </div>
            )}
            <div style={{ ...styles.priceRow, ...styles.priceTotalRow }}>
              <span>You pay now</span>
              <span>₹{summary.razorpay_amount}</span>
            </div>
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: 22 }}>
          <PrimaryButton onClick={handleBookClick} loading={bookingLoading} loadingText="Booking…">
            🙏 Book Now
          </PrimaryButton>
        </div>

        <ErrorBanner message={bookError} />
      </Card>

      {stage === "login" && (
        <LoginModal onClose={() => setStage(null)} onSuccess={() => startBooking()} />
      )}

      {stage === "payment" && (
        <PaymentModal
          title={detail.title}
          price={summary?.razorpay_amount ?? detail.total_payable}
          createOrderFn={() => bookPooja(slug, bookingPayload()).then((res) => res.data)}
          onClose={() => setStage(null)}
          onSuccess={() => {
            setStage(null);
            setBooked(true);
          }}
        />
      )}
    </div>
  );
}

const styles = {
  wrap: { maxWidth: 720, margin: "0 auto" },
  loading: { textAlign: "center", color: "var(--ink-soft)", padding: "40px 0" },
  heroImg: {
    width: "100%", maxHeight: 260, objectFit: "cover",
    borderRadius: "var(--radius)", marginBottom: 16,
  },
  tagsRow: { display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 },
  tag: { fontSize: 11.5, fontWeight: 700, padding: "4px 10px", borderRadius: 999 },
  title: { fontFamily: "var(--font-serif)", fontSize: 24, margin: "0 0 10px", color: "var(--ink)" },
  description: { fontSize: 14.5, color: "var(--ink-soft)", lineHeight: 1.6, marginBottom: 18 },
  schedulingBox: {
    background: "var(--paper-2)",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius)",
    padding: "16px 18px",
    marginBottom: 20
  },
  schedulingHeading: {
    fontSize: 16,
    fontWeight: 700,
    color: "var(--terracotta-dark)",
    margin: "0 0 14px",
    fontFamily: "var(--font-serif)"
  },
  formGroup: {
    marginBottom: 12
  },
  label: {
    display: "block",
    fontSize: 13,
    fontWeight: 700,
    color: "var(--ink)",
    marginBottom: 6
  },
  selectInput: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "var(--radius-sm)",
    border: "1px solid var(--line)",
    fontSize: 14,
    background: "#fff"
  },
  dateTimeInput: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "var(--radius-sm)",
    border: "1px solid var(--line)",
    fontSize: 14,
    background: "#fff"
  },
  benefits: { marginBottom: 18 },
  benefitsTitle: { fontWeight: 700, color: "var(--terracotta-dark)", fontSize: 13.5, marginBottom: 8 },
  benefitsList: { listStyle: "none", padding: 0, margin: 0 },
  benefitItem: { fontSize: 14, lineHeight: 1.6, marginBottom: 6, color: "var(--ink)" },
  priceBox: {
    background: "var(--paper-2)", borderRadius: "var(--radius)", padding: "14px 16px",
    marginBottom: 14,
  },
  priceRow: { display: "flex", justifyContent: "space-between", fontSize: 14, padding: "4px 0" },
  priceTotalRow: { fontWeight: 700, borderTop: "1px solid var(--line)", marginTop: 6, paddingTop: 10 },
  couponRow: { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 8 },
  couponInput: {
    flex: 1, minWidth: 180, padding: "10px 12px", borderRadius: "var(--radius-sm)",
    border: "1px solid var(--line)", fontSize: 14, boxSizing: "border-box",
  },
  walletLabel: { display: "flex", alignItems: "center", gap: 6, fontSize: 13.5, color: "var(--ink)" },
  summaryBox: {
    background: "var(--paper-2)", borderRadius: "var(--radius)", padding: "12px 16px",
    marginTop: 10,
  },
  summaryError: { color: "var(--danger)", fontSize: 13, marginBottom: 6 },
  successBox: {
    background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.5)",
    color: "#166534", borderRadius: 10, padding: "16px", fontSize: 14.5, lineHeight: 1.6,
  },
};
